/* Il costo orario dei dipendenti: serve al controllo di gestione per dire quanto resta.
 *
 * Dal 15 ottobre 2026. E' il numero che trasforma "quanto fatturiamo" in "quanto resta":
 * con il costo di ogni ora, la scheda Gestione mostra il margine di pratiche, clienti e
 * persone. **Un costo che cambia e' una riga nuova con una data nuova**, come le tariffe:
 * un aumento vale da quel giorno. **Chi non ha un costo si vede subito**: finche' manca, il
 * margine di chi ha lavorato non e' completo, e la scheda Gestione lo dice invece di
 * mostrare un numero sbagliato.
 *
 * **Solo l'amministratore.** E' un dato delicato: non compare in nessuna schermata di chi
 * imputa le ore o di chi fattura, e il server lo nega a entrambi.
 */

import { useState, type FormEvent } from "react";
import { ErroreApi, costi } from "../api";
import { useCarica } from "../carica";
import { dataCompleta, euro, importoDaScritto, oggi, type PersonaCosti } from "../dominio";

function messaggio(e: unknown, ripiego: string): string {
  return e instanceof ErroreApi ? e.message : ripiego;
}

export function Costi() {
  const dati = useCarica<PersonaCosti[]>(() => costi.elenco(), []);
  const [aperta, setAperta] = useState<string | null>(null);

  if (dati.errore) {
    return (
      <p className="errore" role="alert">
        {dati.errore}
      </p>
    );
  }
  if (dati.dati === undefined) return <p className="attesa">Un momento.</p>;
  const persone = dati.dati;
  const senza = persone.filter((p) => p.attivo && p.in_vigore === null);

  return (
    <section className="costi">
      <p className="aiuto">
        Il costo pieno di un'ora: stipendio, contributi, TFR e, se vuoi, una quota delle spese di studio. Lo
        vede solo l'amministratore.
      </p>
      {senza.length > 0 && (
        <p className="riga-avviso">
          Manca il costo di {senza.map((p) => p.nome).join(", ")}: finché manca, il margine del loro lavoro non
          si calcola.
        </p>
      )}
      <table className="tabella costi-tabella">
        <thead>
          <tr>
            <th>Persona</th>
            <th className="cifra">Costo orario di oggi</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {persone.map((p) => (
            <RigaPersona
              key={p.persona}
              p={p}
              aperta={aperta === p.persona}
              apri={() => setAperta(aperta === p.persona ? null : p.persona)}
              dopo={async () => {
                await dati.ricarica();
              }}
            />
          ))}
        </tbody>
      </table>
    </section>
  );
}

function RigaPersona({
  p,
  aperta,
  apri,
  dopo,
}: {
  p: PersonaCosti;
  aperta: boolean;
  apri: () => void;
  dopo: () => Promise<void>;
}) {
  return (
    <>
      <tr>
        <td>
          <span className="pratica-nome">{p.nome}</span>
          <span className="narrativa">
            {p.ruolo.charAt(0) + p.ruolo.slice(1).toLowerCase()}
            {!p.attivo && ", disattivato"}
          </span>
        </td>
        <td className="cifra">
          {p.in_vigore === null ? <span className="urgenza-avviso">manca</span> : `${euro(p.in_vigore)} €`}
        </td>
        <td className="colonna-azione">
          <button type="button" className="tasto" onClick={apri} aria-expanded={aperta}>
            {aperta ? "Chiudi" : p.in_vigore === null ? "Imposta" : "Cambia"}
          </button>
        </td>
      </tr>
      {aperta && (
        <tr>
          <td colSpan={3}>
            <Modulo p={p} dopo={dopo} />
          </td>
        </tr>
      )}
    </>
  );
}

function Modulo({ p, dopo }: { p: PersonaCosti; dopo: () => Promise<void> }) {
  // Il primo costo vale da inizio anno: le ore gia' scritte devono averne uno. Un cambio vale da oggi.
  const primo = p.costi.length === 0;
  const [cifra, setCifra] = useState("");
  const [dal, setDal] = useState(primo ? `${new Date().getFullYear()}-01-01` : oggi());
  const [nota, setNota] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  async function salva(evento: FormEvent) {
    evento.preventDefault();
    setErrore(null);
    const letta = importoDaScritto(cifra);
    if (letta === null) return setErrore("Scrivi i euro all'ora, per esempio 24 oppure 24,50.");
    setInCorso(true);
    try {
      await costi.imposta(p.persona, letta, dal, nota.trim());
      setCifra("");
      setNota("");
      await dopo();
    } catch (e) {
      setErrore(messaggio(e, "Non sono riuscito a salvare il costo."));
    } finally {
      setInCorso(false);
    }
  }

  async function togli(id: number) {
    setErrore(null);
    try {
      await costi.togli(id);
      await dopo();
    } catch (e) {
      setErrore(messaggio(e, "Non sono riuscito a togliere il costo."));
    }
  }

  return (
    <div className="costi-modulo">
      <form className="modulo" onSubmit={salva} noValidate>
        <div className="imputa-riga">
          <div className="campo campo-minuti">
            <label htmlFor={`costo-${p.persona}`}>Euro all'ora</label>
            <input
              id={`costo-${p.persona}`}
              inputMode="decimal"
              value={cifra}
              placeholder="per esempio 24,50"
              onChange={(e) => setCifra(e.target.value)}
            />
          </div>
          <div className="campo campo-data">
            <label htmlFor={`costo-dal-${p.persona}`}>Vale dal</label>
            <input
              id={`costo-dal-${p.persona}`}
              type="date"
              value={dal}
              required
              onChange={(e) => setDal(e.target.value)}
            />
          </div>
          <div className="campo">
            <label htmlFor={`costo-nota-${p.persona}`}>Nota, se serve</label>
            <input
              id={`costo-nota-${p.persona}`}
              value={nota}
              maxLength={300}
              placeholder="per esempio nuovo contratto"
              onChange={(e) => setNota(e.target.value)}
            />
          </div>
          <button className="tasto tasto-principale" type="submit" disabled={inCorso}>
            {inCorso ? "Sto salvando" : "Salva"}
          </button>
        </div>
      </form>
      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}
      {p.costi.length > 0 && (
        <ul className="costi-storia">
          {p.costi.map((c) => (
            <li key={c.id}>
              <span>
                dal {dataCompleta(c.dal)}: <strong>{euro(c.oraria)} €</strong> all'ora
                {c.nota && <span className="narrativa"> {c.nota}</span>}
              </span>
              <button type="button" className="tasto tasto-nudo" onClick={() => void togli(c.id)}>
                Togli
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
